
import { Button } from '../../../components/base/Button';

const services = [
  {
    id: 1,
    icon: 'ri-building-line',
    title: 'Property Development',
    description: 'Full-service real estate development from concept to completion, including luxury residential, commercial, and mixed-use projects.',
    features: ['Land Acquisition', 'Design & Planning', 'Construction Management', 'Sales & Marketing']
  },
  {
    id: 2,
    icon: 'ri-line-chart-line',
    title: 'Investment Consulting',
    description: 'Expert guidance on real estate investment opportunities with comprehensive market analysis and risk assessment.',
    features: ['Market Analysis', 'Investment Strategy', 'Risk Assessment', 'Portfolio Management']
  },
  {
    id: 3,
    icon: 'ri-settings-3-line',
    title: 'Project Management',
    description: 'Professional project management services ensuring timely delivery and quality execution of development projects.',
    features: ['Timeline Management', 'Quality Control', 'Budget Oversight', 'Stakeholder Coordination']
  },
  {
    id: 4,
    icon: 'ri-home-line',
    title: 'Property Sales',
    description: 'Comprehensive real estate sales services for residential and commercial properties with premium marketing strategies.',
    features: ['Property Marketing', 'Sales Negotiations', 'Client Relations', 'Market Positioning']
  },
  {
    id: 5,
    icon: 'ri-map-line',
    title: 'Land Acquisition',
    description: 'Strategic land acquisition services identifying prime development opportunities in high-growth markets.',
    features: ['Site Analysis', 'Due Diligence', 'Negotiation', 'Legal Compliance']
  },
  {
    id: 6,
    icon: 'ri-group-line',
    title: 'Real Estate Consulting',
    description: 'Strategic consulting services for investors, developers, and institutions in the real estate sector.',
    features: ['Strategic Planning', 'Market Research', 'Feasibility Studies', 'Investment Advisory']
  }
];

export const ServicesSection = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive real estate development and investment services designed to maximize 
            returns and deliver exceptional results for all stakeholders.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-teal-600 transition-colors">
                <i className={`${service.icon} text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
              </div>
              
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
              <p className="text-gray-600 mb-6">{service.description}</p>
              
              <div className="space-y-2 mb-6">
                {service.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-3 text-sm"></i>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                variant="outline" 
                fullWidth 
                className="border-teal-200 text-teal-700 hover:bg-teal-50"
                onClick={() => scrollToSection('contact')}
              >
                Learn More
              </Button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">
                Why Choose Avalar Realty Holdings?
              </h3>
              <p className="text-gray-600 mb-8">
                Founded in 2024, we bring fresh perspectives and innovative approaches to real estate development and investment, 
                combining modern strategies with industry best practices to deliver exceptional 
                results for our clients and investors.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-award-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Industry Leadership</h4>
                    <p className="text-gray-600 text-sm">
                      Recognized as a leader in luxury real estate development with numerous industry awards and accolades.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Proven Track Record</h4>
                    <p className="text-gray-600 text-sm">
                      Successful developments and consistently delivering above-market returns to investors.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-customer-service-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Client-Focused Approach</h4>
                    <p className="text-gray-600 text-sm">
                      Dedicated to building long-term relationships and providing personalized service to meet unique client needs.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/49d33426c308482b0caa2a11f5807bf5.jpg"
                alt="Diverse Professional Team"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
